const assetModules = import.meta.glob(['../../assets/**/*.jpg', '../../assets/**/*.jpeg', '../../assets/**/*.png', '../../assets/**/*.webp'], {
  eager: true,
  query: '?url',
  import: 'default',
})

const asset = (path) => {
  const url = assetModules[`../../assets/${path}`]

  if (!url) {
    throw new Error(`Missing WebP asset: ${path}`)
  }

  return url
}

const neutralColorways = [
  { suffix: 'white', nameZh: '白色', nameEn: 'White', nameJa: 'ホワイト', nameKr: '화이트' },
  { suffix: 'grey', nameZh: '浅灰', nameEn: 'Light Grey', nameJa: 'ライトグレー', nameKr: '라이트 그레이' },
  { suffix: 'cream', nameZh: '浅米', nameEn: 'Light Cream', nameJa: 'ライトクリーム', nameKr: '라이트 크림' },
]

export const productModules = [
  {
    id: 'bedding',
    nameZh: '四件套',
    nameEn: 'Bedding Set',
    nameJa: 'ベッドセット',
    nameKr: '침구 세트',
    descriptionZh: '先选面料，再选择该面料对应的白色、浅灰或浅米方案。',
    descriptionEn: 'Choose a fabric first, then select its white, light grey, or light cream colorway.',
  },
  {
    id: 'bathrobe',
    nameZh: '浴袍',
    nameEn: 'Bathrobe',
    nameJa: 'バスローブ',
    nameKr: '가운',
    descriptionZh: '仅展示浴袍面料参考图，成图时会自动按酒店浴袍陈列方式生成。',
    descriptionEn: 'Display material swatches only. The final render will place the robe in a standard hotel presentation.',
  },
]

export const beddingFabrics = [
  {
    id: 'wave',
    nameZh: '水波纹',
    nameEn: 'Wave',
    nameJa: 'ウェーブ',
    nameKr: '웨이브',
    preview: asset('sijiantao/Surface/水波纹-Wave.png'),
    variantIds: ['wave-white', 'wave-grey', 'wave-cream'],
    sizeHintZh: '水波纹的起伏节奏、纹理间距和方向要按参考图呈现，不能变成其他暗纹。',
    sizeHintEn: 'Match the wave cadence, spacing, and direction to the reference; do not replace it with another subtle texture.',
  },
  {
    id: 'sateen',
    nameZh: '缎纹',
    nameEn: 'Sateen',
    nameJa: 'サテン',
    nameKr: '새틴',
    preview: asset('sijiantao/Surface/缎纹-Sateen.jpg'),
    variantIds: [
      'sateen-grey',
      'sateen-white',
      'sateen-cream',
      'sateen-print-1',
      'sateen-print-2',
      'sateen-print-3',
      'sateen-print-4',
      'sateen-print-5',
    ],
    sizeHintZh: '缎纹要保留真实的缎面光泽和细腻纹理；如果选择印花，花型比例和布局必须完全跟随参考图。',
    sizeHintEn: 'Preserve the real sateen sheen and fine texture; when a print is selected, keep its scale and layout exactly aligned with the reference.',
  },
  {
    id: 'window-grid',
    nameZh: '回字格',
    nameEn: 'Window Grid',
    nameJa: '窓格子',
    nameKr: '창문 격자',
    preview: asset('sijiantao/Surface/回字格-Window.png'),
    sizeReferencePreview: asset('sijiantao/回字格大小参考.png'),
    sizeReferenceNameZh: '回字格大小参考',
    sizeReferenceNameEn: 'Window Grid Size Reference',
    variantIds: ['window-grid-white', 'window-grid-grey', 'window-grid-cream'],
    sizeHintZh: '回字格的规格先按1*1厘米的小格纹样约束生成，保持非常密集细腻的视觉效果。',
    sizeHintEn: 'Constrain the window-grid to a very dense motif around 1 cm for generation so the final result does not enlarge into oversized blocks.',
  },
  {
    id: 'stripe-fine',
    nameZh: '1厘米条纹',
    nameEn: '1cm Stripe',
    nameJa: '1cmストライプ',
    nameKr: '1cm 스트라이프',
    preview: asset('sijiantao/Surface/条纹-1cm Stripe.png'),
    modelReferencePreview: asset('sijiantao/1cm条纹参考图.png'),
    variantIds: ['stripe-fine-white', 'stripe-fine-grey', 'stripe-fine-cream'],
    sizeHintZh: '条纹宽度严格按 1 厘米呈现，不能误做成粗条纹。',
    sizeHintEn: 'Render the stripe width strictly at 1 cm; do not turn it into a wide stripe.',
  },
  {
    id: 'stripe-classic',
    nameZh: '2厘米条纹',
    nameEn: '2cm Stripe',
    nameJa: '2cmストライプ',
    nameKr: '2cm 스트라이프',
    preview: asset('sijiantao/Surface/条纹-1cm Stripe.jpg'),
    modelReferencePreview: asset('sijiantao/条纹2-3cm参考图.png'),
    variantIds: ['stripe-classic-white', 'stripe-classic-grey', 'stripe-classic-cream'],
    sizeHintZh: '条纹宽度严格按 2 厘米呈现，条纹疏密、宽窄和浅色层次要按参考图准确保留，不能误做成 1 厘米或 3 厘米条纹。',
    sizeHintEn: 'Render the stripe width strictly at 2 cm and preserve the stripe density, width, and tonal layering from the reference; do not turn it into a 1 cm or 3 cm stripe.',
  },
  {
    id: 'stripe-wide',
    nameZh: '3厘米条纹',
    nameEn: '3cm Stripe',
    nameJa: '3cmストライプ',
    nameKr: '3cm 스트라이프',
    preview: asset('sijiantao/Surface/条纹-3cm Stripe.jpg'),
    modelReferencePreview: asset('sijiantao/条纹2-3cm参考图.png'),
    variantIds: ['stripe-wide-white', 'stripe-wide-grey', 'stripe-wide-cream'],
    sizeHintZh: '条纹宽度严格按 3 厘米呈现，不能误做成细条纹。',
    sizeHintEn: 'Render the stripe width strictly at 3 cm; do not turn it into a fine stripe.',
  },
  {
    id: 'small-square',
    nameZh: '小方格',
    nameEn: 'Small Square',
    nameJa: '小さな格子',
    nameKr: '작은 사각 격자',
    preview: asset('sijiantao/Surface/小方格-Samll Square.jpg'),
    variantIds: ['small-square-white', 'small-square-grey', 'small-square-cream'],
    sizeHintZh: '小方格纹样要保持细密的小格比例，不能误做成中格或大格。',
    sizeHintEn: 'Keep the motif as a fine small square; do not enlarge it into medium or large squares.',
  },
  {
    id: 'middle-square',
    nameZh: '中方格',
    nameEn: 'Middle Square',
    nameJa: '中格子',
    nameKr: '중간 사각 격자',
    preview: asset('sijiantao/Surface/中方格-Middle Square.jpg'),
    variantIds: ['middle-square-white', 'middle-square-grey', 'middle-square-cream'],
    sizeHintZh: '中方格纹样要保持中等格距，不能误做成小格或大格。',
    sizeHintEn: 'Keep the motif as a medium square grid; do not turn it into small or large squares.',
  },
  {
    id: 'big-square',
    nameZh: '大方格',
    nameEn: 'Big Square',
    nameJa: '大きな格子',
    nameKr: '큰 사각 격자',
    preview: asset('sijiantao/Surface/大方格-Big Square.jpg'),
    variantIds: ['big-square-white', 'big-square-grey', 'big-square-cream'],
    sizeHintZh: '大方格纹样要保持明显的大格比例，不能缩小成中格或小格。',
    sizeHintEn: 'Keep the motif as a clearly large square; do not shrink it into medium or small squares.',
  },
]

export const beddingVariants = [
  {
    id: 'window-grid-white',
    fabricId: 'window-grid',
    nameZh: neutralColorways[0].nameZh,
    nameEn: neutralColorways[0].nameEn,
    nameJa: neutralColorways[0].nameJa,
    nameKr: neutralColorways[0].nameKr,
    preview: asset('sijiantao/Surface/回字格-Window.png'),
    sizeReferencePreview: asset('sijiantao/回字格大小参考-白色.png'),
    sizeReferenceNameZh: '回字格大小参考（白色）',
    sizeReferenceNameEn: 'Window Grid Size Reference (White)',
  },
  {
    id: 'window-grid-grey',
    fabricId: 'window-grid',
    nameZh: neutralColorways[1].nameZh,
    nameEn: neutralColorways[1].nameEn,
    nameJa: neutralColorways[1].nameJa,
    nameKr: neutralColorways[1].nameKr,
    preview: asset('sijiantao/回字格2.jpg'),
    sizeReferencePreview: asset('sijiantao/回字格大小参考.png'),
    sizeReferenceNameZh: '回字格大小参考（浅灰）',
    sizeReferenceNameEn: 'Window Grid Size Reference (Light Grey)',
  },
  {
    id: 'window-grid-cream',
    fabricId: 'window-grid',
    nameZh: neutralColorways[2].nameZh,
    nameEn: neutralColorways[2].nameEn,
    nameJa: neutralColorways[2].nameJa,
    nameKr: neutralColorways[2].nameKr,
    preview: asset('sijiantao/回字格3.jpg'),
    sizeReferencePreview: asset('sijiantao/回字格大小参考-浅米色.png'),
    sizeReferenceNameZh: '回字格大小参考（浅米）',
    sizeReferenceNameEn: 'Window Grid Size Reference (Light Cream)',
  },
  {
    id: 'small-square-white',
    fabricId: 'small-square',
    nameZh: neutralColorways[0].nameZh,
    nameEn: neutralColorways[0].nameEn,
    nameJa: neutralColorways[0].nameJa,
    nameKr: neutralColorways[0].nameKr,
    preview: asset('sijiantao/方格3-1.jpg'),
  },
  {
    id: 'small-square-grey',
    fabricId: 'small-square',
    nameZh: neutralColorways[1].nameZh,
    nameEn: neutralColorways[1].nameEn,
    nameJa: neutralColorways[1].nameJa,
    nameKr: neutralColorways[1].nameKr,
    preview: asset('sijiantao/方格3-2.jpg'),
  },
  {
    id: 'small-square-cream',
    fabricId: 'small-square',
    nameZh: neutralColorways[2].nameZh,
    nameEn: neutralColorways[2].nameEn,
    nameJa: neutralColorways[2].nameJa,
    nameKr: neutralColorways[2].nameKr,
    preview: asset('sijiantao/方格3-3.jpg'),
  },
  {
    id: 'big-square-white',
    fabricId: 'big-square',
    nameZh: neutralColorways[0].nameZh,
    nameEn: neutralColorways[0].nameEn,
    nameJa: neutralColorways[0].nameJa,
    nameKr: neutralColorways[0].nameKr,
    modelReferencePreview: asset('sijiantao/参考图-方格/大方格-白色.png'),
    preview: asset('sijiantao/Surface/大方格-Big Square.jpg'),
  },
  {
    id: 'big-square-grey',
    fabricId: 'big-square',
    nameZh: neutralColorways[1].nameZh,
    nameEn: neutralColorways[1].nameEn,
    nameJa: neutralColorways[1].nameJa,
    nameKr: neutralColorways[1].nameKr,
    modelReferencePreview: asset('sijiantao/参考图-方格/大方格-浅灰.png'),
    preview: asset('sijiantao/方格2-2.jpg'),
  },
  {
    id: 'big-square-cream',
    fabricId: 'big-square',
    nameZh: neutralColorways[2].nameZh,
    nameEn: neutralColorways[2].nameEn,
    nameJa: neutralColorways[2].nameJa,
    nameKr: neutralColorways[2].nameKr,
    promptNameZh: '米色',
    promptNameEn: 'Cream',
    promptNameJa: 'クリーム',
    promptNameKr: '크림',
    modelReferencePreview: asset('sijiantao/参考图-方格/大方格-浅米.png'),
    preview: asset('sijiantao/方格2-3.jpg'),
  },
  {
    id: 'middle-square-white',
    fabricId: 'middle-square',
    nameZh: neutralColorways[0].nameZh,
    nameEn: neutralColorways[0].nameEn,
    nameJa: neutralColorways[0].nameJa,
    nameKr: neutralColorways[0].nameKr,
    modelReferencePreview: asset('sijiantao/参考图-方格/中方格-白色.png'),
    preview: asset('sijiantao/Surface/中方格-Middle Square.jpg'),
  },
  {
    id: 'middle-square-grey',
    fabricId: 'middle-square',
    nameZh: neutralColorways[1].nameZh,
    nameEn: neutralColorways[1].nameEn,
    nameJa: neutralColorways[1].nameJa,
    nameKr: neutralColorways[1].nameKr,
    modelReferencePreview: asset('sijiantao/参考图-方格/中方格-浅灰.png'),
    preview: asset('sijiantao/方格1-2.jpg'),
  },
  {
    id: 'middle-square-cream',
    fabricId: 'middle-square',
    nameZh: neutralColorways[2].nameZh,
    nameEn: neutralColorways[2].nameEn,
    nameJa: neutralColorways[2].nameJa,
    nameKr: neutralColorways[2].nameKr,
    promptNameZh: '米色',
    promptNameEn: 'Cream',
    promptNameJa: 'クリーム',
    promptNameKr: '크림',
    modelReferencePreview: asset('sijiantao/参考图-方格/中方格-米色.png'),
    preview: asset('sijiantao/方格1-3.jpg'),
  },
  {
    id: 'stripe-wide-white',
    fabricId: 'stripe-wide',
    nameZh: neutralColorways[0].nameZh,
    nameEn: neutralColorways[0].nameEn,
    nameJa: neutralColorways[0].nameJa,
    nameKr: neutralColorways[0].nameKr,
    preview: asset('sijiantao/Surface/条纹-3cm Stripe.jpg'),
  },
  {
    id: 'stripe-wide-grey',
    fabricId: 'stripe-wide',
    nameZh: neutralColorways[1].nameZh,
    nameEn: neutralColorways[1].nameEn,
    nameJa: neutralColorways[1].nameJa,
    nameKr: neutralColorways[1].nameKr,
    preview: asset('sijiantao/条纹1-2.jpg'),
  },
  {
    id: 'stripe-wide-cream',
    fabricId: 'stripe-wide',
    nameZh: neutralColorways[2].nameZh,
    nameEn: neutralColorways[2].nameEn,
    nameJa: neutralColorways[2].nameJa,
    nameKr: neutralColorways[2].nameKr,
    promptNameZh: '米色',
    promptNameEn: 'Cream',
    promptNameJa: 'クリーム',
    promptNameKr: '크림',
    preview: asset('sijiantao/条纹1-3.jpg'),
  },
  {
    id: 'stripe-fine-white',
    fabricId: 'stripe-fine',
    nameZh: neutralColorways[0].nameZh,
    nameEn: neutralColorways[0].nameEn,
    nameJa: neutralColorways[0].nameJa,
    nameKr: neutralColorways[0].nameKr,
    preview: asset('sijiantao/Surface/条纹-1cm Stripe.png'),
  },
  {
    id: 'stripe-fine-grey',
    fabricId: 'stripe-fine',
    nameZh: neutralColorways[1].nameZh,
    nameEn: neutralColorways[1].nameEn,
    nameJa: neutralColorways[1].nameJa,
    nameKr: neutralColorways[1].nameKr,
    preview: asset('sijiantao/条纹2-2.jpg'),
  },
  {
    id: 'stripe-fine-cream',
    fabricId: 'stripe-fine',
    nameZh: neutralColorways[2].nameZh,
    nameEn: neutralColorways[2].nameEn,
    nameJa: neutralColorways[2].nameJa,
    nameKr: neutralColorways[2].nameKr,
    promptNameZh: '米色',
    promptNameEn: 'Cream',
    promptNameJa: 'クリーム',
    promptNameKr: '크림',
    modelReferencePreview: asset('sijiantao/1cm条纹米色参考图.png'),
    preview: asset('sijiantao/条纹2-3.jpg'),
  },
  {
    id: 'stripe-classic-white',
    fabricId: 'stripe-classic',
    nameZh: neutralColorways[0].nameZh,
    nameEn: neutralColorways[0].nameEn,
    nameJa: neutralColorways[0].nameJa,
    nameKr: neutralColorways[0].nameKr,
    preview: asset('sijiantao/Surface/条纹-1cm Stripe.jpg'),
  },
  {
    id: 'stripe-classic-grey',
    fabricId: 'stripe-classic',
    nameZh: neutralColorways[1].nameZh,
    nameEn: neutralColorways[1].nameEn,
    nameJa: neutralColorways[1].nameJa,
    nameKr: neutralColorways[1].nameKr,
    preview: asset('sijiantao/条纹3-2.jpg'),
  },
  {
    id: 'stripe-classic-cream',
    fabricId: 'stripe-classic',
    nameZh: neutralColorways[2].nameZh,
    nameEn: neutralColorways[2].nameEn,
    nameJa: neutralColorways[2].nameJa,
    nameKr: neutralColorways[2].nameKr,
    promptNameZh: '米色',
    promptNameEn: 'Cream',
    promptNameJa: 'クリーム',
    promptNameKr: '크림',
    preview: asset('sijiantao/条纹3-cream.jpg'),
  },
  {
    id: 'wave-white',
    fabricId: 'wave',
    nameZh: neutralColorways[0].nameZh,
    nameEn: neutralColorways[0].nameEn,
    nameJa: neutralColorways[0].nameJa,
    nameKr: neutralColorways[0].nameKr,
    preview: asset('sijiantao/Surface/水波纹-Wave.png'),
  },
  {
    id: 'wave-grey',
    fabricId: 'wave',
    nameZh: neutralColorways[1].nameZh,
    nameEn: neutralColorways[1].nameEn,
    nameJa: neutralColorways[1].nameJa,
    nameKr: neutralColorways[1].nameKr,
    preview: asset('sijiantao/水波纹2.jpg'),
  },
  {
    id: 'wave-cream',
    fabricId: 'wave',
    nameZh: neutralColorways[2].nameZh,
    nameEn: neutralColorways[2].nameEn,
    nameJa: neutralColorways[2].nameJa,
    nameKr: neutralColorways[2].nameKr,
    preview: asset('sijiantao/水波纹3.jpg'),
  },
  {
    id: 'sateen-grey',
    fabricId: 'sateen',
    nameZh: '灰色',
    nameEn: 'Grey',
    nameJa: 'グレー',
    nameKr: '그레이',
    preview: asset('sijiantao/color-flower/灰色-Grey.jpg'),
  },
  {
    id: 'sateen-white',
    fabricId: 'sateen',
    nameZh: '白色',
    nameEn: 'White',
    nameJa: 'ホワイト',
    nameKr: '화이트',
    preview: asset('sijiantao/color-flower/白色-White.jpg'),
  },
  {
    id: 'sateen-cream',
    fabricId: 'sateen',
    nameZh: '米色',
    nameEn: 'Cream',
    nameJa: 'クリーム',
    nameKr: '크림',
    preview: asset('sijiantao/color-flower/米色-Cream.jpg'),
  },
  {
    id: 'sateen-print-1',
    fabricId: 'sateen',
    nameZh: '印花 1',
    nameEn: 'Print 1',
    nameJa: 'プリント 1',
    nameKr: '프린트 1',
    preview: asset('sijiantao/color-flower/印花1.png'),
  },
  {
    id: 'sateen-print-2',
    fabricId: 'sateen',
    nameZh: '印花 2',
    nameEn: 'Print 2',
    nameJa: 'プリント 2',
    nameKr: '프린트 2',
    preview: asset('sijiantao/color-flower/印花2.jpg'),
  },
  {
    id: 'sateen-print-3',
    fabricId: 'sateen',
    nameZh: '印花 3',
    nameEn: 'Print 3',
    nameJa: 'プリント 3',
    nameKr: '프린트 3',
    preview: asset('sijiantao/color-flower/印花3.jpg'),
  },
  {
    id: 'sateen-print-4',
    fabricId: 'sateen',
    nameZh: '印花 4',
    nameEn: 'Print 4',
    nameJa: 'プリント 4',
    nameKr: '프린트 4',
    preview: asset('sijiantao/color-flower/印花4.jpg'),
  },
  {
    id: 'sateen-print-5',
    fabricId: 'sateen',
    nameZh: '印花 5',
    nameEn: 'Print 5',
    nameJa: 'プリント 5',
    nameKr: '프린트 5',
    preview: asset('sijiantao/color-flower/印花5.jpg'),
  },
]

export const bathrobeOptions = [
  {
    id: 'bathrobe-terry',
    nameZh: '毛圈白',
    nameEn: 'Terry White',
    nameJa: 'タオル地ホワイト',
    nameKr: '터치 화이트',
    preview: asset('yupao/Terry.png'),
    swatch: asset('yupao/Surface/Terry.jpg'),
  },
  {
    id: 'bathrobe-waffle',
    nameZh: '华夫格',
    nameEn: 'Waffle',
    nameJa: 'ワッフル',
    nameKr: '와플',
    preview: asset('yupao/Waffle.jpg'),
    swatch: asset('yupao/Surface/Waffle.jpg'),
  },
  {
    id: 'bathrobe-microfiber-cream',
    nameZh: '超细纤维浅米',
    nameEn: 'Microfiber Light Cream',
    nameJa: 'マイクロファイバーライトクリーム',
    nameKr: '마이크로파이버 라이트 크림',
    preview: asset('yupao/Microfiber Cream.jpg'),
    swatch: asset('yupao/Surface/Microfiber Cream.png'),
  },
  {
    id: 'bathrobe-microfiber-grey',
    nameZh: '超细纤维浅灰',
    nameEn: 'Microfiber Light Grey',
    nameJa: 'マイクロファイバーライトグレー',
    nameKr: '마이크로파이버 라이트 그레이',
    preview: asset('yupao/Microfiber Grey.png'),
    swatch: asset('yupao/Surface/Microfiber Grey.png'),
  },
  {
    id: 'bathrobe-printing-1',
    nameZh: '印花面料一',
    nameEn: 'Printed Material A',
    nameJa: 'プリント生地 A',
    nameKr: '프린트 원단 A',
    preview: asset('yupao/Printing1.png'),
    swatch: asset('yupao/Surface/Printing1.jpg'),
  },
  {
    id: 'bathrobe-printing-2',
    nameZh: '印花面料二',
    nameEn: 'Printed Material B',
    nameJa: 'プリント生地 B',
    nameKr: '프린트 원단 B',
    preview: asset('yupao/Printing2.png'),
    swatch: asset('yupao/Surface/Printing2.jpg'),
  },
]

export const getBeddingFabricById = (fabricId) =>
  beddingFabrics.find((fabric) => fabric.id === fabricId)

export const getBeddingVariantsByFabric = (fabricId) =>
  beddingVariants.filter((variant) => variant.fabricId === fabricId)

export const getBathrobeOptionById = (optionId) =>
  bathrobeOptions.find((option) => option.id === optionId)
